/**
 * Construit le user_context envoyé à l'API Laika (testable sans monter ChatWidget).
 */

import type { LaikaPageContext } from './buildLaikaPageContext';
import { isValidOnboardingTimestamp } from './onboardingStorage';
import {
  buildVisibleSidebarNav,
  isAssociationSidebarPage,
  type VisibleNavId,
  type VisibleSidebarNavFlags,
} from './visibleSidebarNav';

export type LaikaChatUserFields = {
  user_id?: string | number | null;
  name?: string | null;
  email?: string | null;
  is_premium?: boolean | null;
  pro_id?: number | null;
  onboarding_completed_at?: string | null;
  stripe_account_id?: string | null;
};

export type LaikaChatUserContext = {
  user_id?: string | number | null;
  name?: string;
  email?: string;
  is_premium?: boolean;
  subscription?: string;
  pro_id?: number | null;
  business_data_enabled: boolean;
  page_context: LaikaPageContext;
  /** Nav réelle : false = menu Démarrage masqué (compte établi). Toujours envoyé (N1+N2). */
  onboarding_completed: boolean;
  startup_tab_visible: boolean;
  /** N2 only — booléen, jamais l'id Stripe. */
  stripe_connected?: boolean;
  /** Sidebar réelle (ids stables, N1+N2). */
  visible_nav: VisibleNavId[];
};

export type LaikaSidebarNavHints = Pick<
  VisibleSidebarNavFlags,
  'boardingVisible' | 'iosFreeUser' | 'billingUi' | 'marketingTools' | 'followEngine' | 'associationMessaging'
>;

export function buildLaikaChatUserContext(args: {
  laikaDataAccessEnabled: boolean;
  user?: LaikaChatUserFields | null;
  pageContext: LaikaPageContext;
  sidebarNav?: LaikaSidebarNavHints;
}): LaikaChatUserContext {
  const { laikaDataAccessEnabled, user, pageContext, sidebarNav } = args;
  const onboardingCompleted = isValidOnboardingTimestamp(user?.onboarding_completed_at);
  const startupTabVisible = !onboardingCompleted;
  const navFlags = {
    onboarding_completed: onboardingCompleted,
    startup_tab_visible: startupTabVisible,
    visible_nav: buildVisibleSidebarNav({
      startupTabVisible,
      associationMode: isAssociationSidebarPage(pageContext.page),
      boardingVisible: sidebarNav?.boardingVisible,
      iosFreeUser: sidebarNav?.iosFreeUser,
      billingUi: sidebarNav?.billingUi,
      marketingTools: sidebarNav?.marketingTools,
      followEngine: sidebarNav?.followEngine,
      associationMessaging: sidebarNav?.associationMessaging,
    }),
  };

  if (laikaDataAccessEnabled) {
    const stripeId = user?.stripe_account_id;
    return {
      user_id: user?.user_id,
      name: user?.name ?? undefined,
      email: user?.email ?? undefined,
      is_premium: !!user?.is_premium,
      subscription: user?.is_premium ? 'Premium' : 'Standard',
      pro_id: user?.pro_id ?? null,
      business_data_enabled: true,
      page_context: pageContext,
      ...navFlags,
      ...(typeof stripeId === 'string' && stripeId.trim()
        ? { stripe_connected: true }
        : {}),
    };
  }

  return {
    user_id: user?.user_id,
    business_data_enabled: false,
    page_context: pageContext,
    ...navFlags,
  };
}

export function buildLaikaChatRequestBody(args: {
  message: string;
  conversationId?: string | null;
  laikaDataAccessEnabled: boolean;
  user?: LaikaChatUserFields | null;
  pageContext: LaikaPageContext;
  sidebarNav?: LaikaSidebarNavHints;
}): {
  message: string;
  conversation_id?: string | null;
  user_context: LaikaChatUserContext;
} {
  return {
    message: args.message,
    conversation_id: args.conversationId ?? undefined,
    user_context: buildLaikaChatUserContext({
      laikaDataAccessEnabled: args.laikaDataAccessEnabled,
      user: args.user,
      pageContext: args.pageContext,
      sidebarNav: args.sidebarNav,
    }),
  };
}
